import React from 'react';

import { Link } from '~/lib/react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { VoucherLog } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { VoucherLogDisplayMode, isVoucherLogProvideCredit } from '~/utils/voucherLogDisplayMode';

import CopyableUserLink from '../Common/CopyableUserLink';
import DebugModal from '../DebugModal/DebugModal';

import ProvideCreditAmountStatus from './ProvideCreditAmountStatus';

export const voucherLogColumnSharedAttrs: Pick<
  GridColDef,
  'sortable' | 'display' | 'flex' | 'editable' | 'filterable'
> = {
  sortable: false,
  display: 'flex',
  flex: 1,
  filterable: false,
  editable: false,
};

type UserSummaryLite = { full_name?: string; email?: string };
type VoucherLite = { voucherName?: string };

/** Last credit-grant apply response for inline debug on the voucher log row. */
export type CreditGrantApplyLastResult = {
  voucherLogId: string;
  outcome: 'success' | 'error';
  data?: unknown;
  error?: unknown;
};

/** Last successful credit-grant dry-run response for review before Apply. */
export type CreditGrantDryRunLastResult = {
  voucherLogId: string;
  data: unknown;
};

export type BuildVoucherLogColumnsOptions = {
  usersSummaryMap: Map<string, UserSummaryLite>;
  vouchersMap: Map<string, VoucherLite>;
  onUserIdClick?: (userId: string) => void;
  onVoucherClick?: (voucherName: string) => void;
  onUnlog?: (log: VoucherLog) => void;
  unlogPending?: boolean;
  /** When false, omits the trailing unlog actions column (e.g. compact dialog grids). */
  includeUnlogColumn?: boolean;
  /** When false, omits the credit-grant process column (e.g. nested dialog grids). */
  includeProcessCreditGrantColumn?: boolean;
  /** Starts a credit-grant dry run for this log row. */
  onProcessCreditGrant?: (log: VoucherLog) => void;
  /** Persists the credit grant after a successful dry run for this row. */
  onApplyCreditGrant?: (log: VoucherLog) => void;
  /** When false, Process is shown disabled with a tooltip (column still renders). */
  processCreditGrantAllowed?: boolean;
  /** True while any credit-grant dry run or apply request is in flight. */
  processCreditGrantPending?: boolean;
  /** Computed from loaded log rows; controls discount vs credit payout labels and columns. */
  displayMode: VoucherLogDisplayMode;
  /** While a dry run runs for this log id, the Process column shows a loading state. */
  creditGrantDryRunLoadingLogId?: string | null;
  /** After dry run succeeds for this log id, Apply is shown next to Process. */
  creditGrantDryRunReadyLogId?: string | null;
  /** While apply runs, this log row shows a loading state in the Process column. */
  creditGrantProcessingLogId?: string | null;
  /** Last apply outcome for a row; enables the “Credit grant” debug modal on that row until cleared. */
  creditGrantLastResult?: CreditGrantApplyLastResult | null;
  /** Last successful dry-run payload for a row; enables “Dry run result” before Apply. */
  creditGrantDryRunLastResult?: CreditGrantDryRunLastResult | null;
};

export type ProcessCreditGrantColumnOptions = Pick<
  BuildVoucherLogColumnsOptions,
  | 'onProcessCreditGrant'
  | 'onApplyCreditGrant'
  | 'processCreditGrantAllowed'
  | 'processCreditGrantPending'
  | 'creditGrantDryRunLoadingLogId'
  | 'creditGrantDryRunReadyLogId'
  | 'creditGrantProcessingLogId'
  | 'creditGrantLastResult'
  | 'creditGrantDryRunLastResult'
>;

/** Credit-grant column: Process runs dry run; after success, Apply appears beside Process. */
export function buildProcessCreditGrantColumnDef({
  onProcessCreditGrant,
  onApplyCreditGrant,
  processCreditGrantAllowed = true,
  processCreditGrantPending = false,
  creditGrantDryRunLoadingLogId = null,
  creditGrantDryRunReadyLogId = null,
  creditGrantProcessingLogId = null,
  creditGrantLastResult = null,
  creditGrantDryRunLastResult = null,
}: ProcessCreditGrantColumnOptions): GridColDef<VoucherLog> {
  return {
    field: 'processCreditGrant',
    headerName: 'Process',
    minWidth: 200,
    width: 200,
    ...voucherLogColumnSharedAttrs,
    flex: undefined,
    renderCell: (params: GridRenderCellParams<VoucherLog>) => {
      const row = params.row;
      const orderId = row.fkOrder ?? row.fk_order;
      const creditGranted = row.creditGrantedAt ?? row.metadata?.creditGrantedAt;
      const rowIdStr = String(row.voucherLogId ?? '');
      const hasDebug = creditGrantLastResult != null && String(creditGrantLastResult.voucherLogId) === rowIdStr;
      const hasDryRunDebug =
        creditGrantDryRunLastResult != null && String(creditGrantDryRunLastResult.voucherLogId) === rowIdStr;
      const isRowApplying = creditGrantProcessingLogId != null && String(creditGrantProcessingLogId) === rowIdStr;
      const isDryRunLoading =
        creditGrantDryRunLoadingLogId != null && String(creditGrantDryRunLoadingLogId) === rowIdStr;
      const isDryRunReady = creditGrantDryRunReadyLogId != null && String(creditGrantDryRunReadyLogId) === rowIdStr;

      if (row.deletedAt || orderId == null || String(orderId).trim() === '' || !isVoucherLogProvideCredit(row)) {
        return null;
      }
      if (creditGranted && !hasDebug) {
        return null;
      }

      if (isRowApplying) {
        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 32 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Applying…
            </Typography>
          </Stack>
        );
      }

      if (isDryRunLoading) {
        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 32 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Preview…
            </Typography>
          </Stack>
        );
      }

      const gridBusy = processCreditGrantPending;
      const canClickProcess = processCreditGrantAllowed && !gridBusy && !creditGranted;
      const canClickApply =
        processCreditGrantAllowed && !gridBusy && isDryRunReady && onApplyCreditGrant != null && !creditGranted;

      const dryRunDebugModal =
        hasDryRunDebug && creditGrantDryRunLastResult ? (
          <DebugModal
            type="generic"
            title="Dry run result"
            hideIcon
            data={{
              outcome: 'success',
              response: creditGrantDryRunLastResult.data,
            }}
          />
        ) : null;

      const debugModal = hasDebug ? (
        <DebugModal
          type="generic"
          title="Credit grant"
          hideIcon
          data={{
            outcome: creditGrantLastResult.outcome,
            ...(creditGrantLastResult.outcome === 'success'
              ? { response: creditGrantLastResult.data }
              : { error: creditGrantLastResult.error }),
          }}
        />
      ) : null;

      const processButton = (
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onProcessCreditGrant?.(row);
          }}
          disabled={!canClickProcess}
          sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
        >
          Process
        </Button>
      );

      const applyButton = isDryRunReady ? (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onApplyCreditGrant?.(row);
          }}
          disabled={!canClickApply}
          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
        >
          Apply
        </Button>
      ) : null;

      const dryRunSucceeded = isDryRunReady || hasDryRunDebug;
      const dryRunSuccessTick = dryRunSucceeded ? (
        <Tooltip title="Dry run succeeded">
          <CheckCircleIcon color="success" fontSize="small" sx={{ flexShrink: 0, display: 'block' }} />
        </Tooltip>
      ) : null;

      if (creditGranted && hasDebug) {
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
            {debugModal}
          </Stack>
        );
      }

      const processCell = !processCreditGrantAllowed ? (
        <Tooltip title="Promo Admin role is required to process credit grants.">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {processButton}
          </Box>
        </Tooltip>
      ) : (
        processButton
      );

      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
          {processCell}
          {applyButton}
          {dryRunSuccessTick}
          {dryRunDebugModal}
          {debugModal}
        </Stack>
      );
    },
  };
}

export function buildVoucherLogColumns({
  usersSummaryMap,
  vouchersMap,
  onUserIdClick,
  onVoucherClick,
  onUnlog,
  unlogPending = false,
  includeUnlogColumn = true,
  includeProcessCreditGrantColumn = true,
  onProcessCreditGrant,
  onApplyCreditGrant,
  processCreditGrantAllowed = true,
  processCreditGrantPending = false,
  creditGrantDryRunLoadingLogId = null,
  creditGrantDryRunReadyLogId = null,
  creditGrantProcessingLogId = null,
  creditGrantLastResult = null,
  creditGrantDryRunLastResult = null,
  displayMode,
}: BuildVoucherLogColumnsOptions): Array<GridColDef<VoucherLog>> {
  const discountAmountRender = (params: GridRenderCellParams<VoucherLog>) => {
    const currency = params.row.metadata?.voucherCurrency ?? params.row.currencyCode;
    const remaining = params.row.metadata?.remainingAmountAfter;
    return (
      <>
        {currencyFormatter(currency, params.row.amount)}{' '}
        {remaining != null && remaining > 0 ? `(${currencyFormatter(currency, remaining)} left)` : ''}
      </>
    );
  };

  const creditPayoutAmountRender = (params: GridRenderCellParams<VoucherLog>) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 0.25,
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {currencyFormatter(params.row.metadata?.voucherCurrency ?? params.row.currencyCode, params.row.amount)} Credit
      </Box>
      <ProvideCreditAmountStatus row={params.row} />
    </Box>
  );

  const emptyCell = (
    <Typography variant="body2" color="text.secondary" component="span">
      —
    </Typography>
  );

  const amountAndCreditColumns: Array<GridColDef<VoucherLog>> = (() => {
    if (displayMode === 'discount-only') {
      return [
        {
          field: 'amount',
          headerName: 'Discount amount',
          minWidth: 130,
          flex: 0.5,
          ...voucherLogColumnSharedAttrs,
          renderCell: (params) => discountAmountRender(params),
        },
      ];
    }
    if (displayMode === 'provide-credit-only') {
      return [
        {
          field: 'amount',
          headerName: 'Credit payout',
          minWidth: 130,
          flex: 0.5,
          ...voucherLogColumnSharedAttrs,
          renderCell: (params) => creditPayoutAmountRender(params),
        },
      ];
    }
    return [
      {
        field: 'amount',
        headerName: 'Discount amount',
        minWidth: 130,
        flex: 0.5,
        ...voucherLogColumnSharedAttrs,
        renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? emptyCell : discountAmountRender(params)),
      },
      {
        field: 'creditPayoutAmountDisplay',
        headerName: 'Credit payout',
        minWidth: 130,
        flex: 0.5,
        sortable: false,
        ...voucherLogColumnSharedAttrs,
        valueGetter: (_value, row) => (isVoucherLogProvideCredit(row) ? row.amount : null),
        renderCell: (params) => (isVoucherLogProvideCredit(params.row) ? creditPayoutAmountRender(params) : emptyCell),
      },
    ];
  })();

  const creditPayoutAtColumn: GridColDef<VoucherLog> = {
    field: 'creditPayoutAt',
    headerName: 'Credit payout at',
    minWidth: 180,
    flex: 0.6,
    ...voucherLogColumnSharedAttrs,
    valueGetter: (_value, row) => row.metadata?.creditPayoutAt,
    renderCell: (params) => {
      const payoutAt = params.value as string | undefined;
      if (!payoutAt) {
        return emptyCell;
      }
      const creditGranted = params.row.creditGrantedAt ?? params.row.metadata?.creditGrantedAt;
      const highlightIfPast = !creditGranted && !(new Date(payoutAt) > new Date());
      return <DateFormatterDetailed date={payoutAt} highlightIfPast={highlightIfPast} />;
    },
  };

  const creditGrantedAtColumn: GridColDef<VoucherLog> = {
    field: 'creditGrantedAt',
    headerName: 'Credit granted',
    minWidth: 180,
    flex: 0.6,
    ...voucherLogColumnSharedAttrs,
    valueGetter: (_value, row) => row.creditGrantedAt ?? row.metadata?.creditGrantedAt,
    renderCell: (params) => (params.value ? <DateFormatterDetailed date={params.value as string} /> : emptyCell),
  };

  const creditTimelineColumns: Array<GridColDef<VoucherLog>> =
    displayMode === 'discount-only'
      ? []
      : [
          creditPayoutAtColumn,
          ...(includeProcessCreditGrantColumn && onProcessCreditGrant
            ? [
                buildProcessCreditGrantColumnDef({
                  onProcessCreditGrant,
                  onApplyCreditGrant,
                  processCreditGrantAllowed,
                  processCreditGrantPending,
                  creditGrantDryRunLoadingLogId,
                  creditGrantDryRunReadyLogId,
                  creditGrantProcessingLogId,
                  creditGrantLastResult,
                  creditGrantDryRunLastResult,
                }),
              ]
            : []),
          creditGrantedAtColumn,
        ];

  const processColumnWhenDiscountOnly: Array<GridColDef<VoucherLog>> =
    displayMode === 'discount-only' && includeProcessCreditGrantColumn && onProcessCreditGrant
      ? [
          buildProcessCreditGrantColumnDef({
            onProcessCreditGrant,
            onApplyCreditGrant,
            processCreditGrantAllowed,
            processCreditGrantPending,
            creditGrantDryRunLoadingLogId,
            creditGrantDryRunReadyLogId,
            creditGrantProcessingLogId,
            creditGrantLastResult,
            creditGrantDryRunLastResult,
          }),
        ]
      : [];

  const cols: Array<GridColDef<VoucherLog>> = [
    {
      field: 'fkOrder',
      headerName: 'Order ID',
      minWidth: 60,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.fkOrder}</Box>
          <Tooltip title="Open order">
            <IconButton
              component={Link}
              to={`/purchases/${params.row.fkOrder}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: 'currencyCode', headerName: 'Currency', minWidth: 120, flex: 0.5, ...voucherLogColumnSharedAttrs },
    ...amountAndCreditColumns,
    ...processColumnWhenDiscountOnly,
    ...creditTimelineColumns,
    {
      field: 'voucherId',
      headerName: 'Voucher',
      minWidth: 150,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const voucherId = params.row.voucherId;
        if (!voucherId) {
          return '-';
        }
        const voucher = vouchersMap.get(voucherId);
        const voucherNameForClick = voucher?.voucherName;
        const label = voucherNameForClick ?? voucherId;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {onVoucherClick && voucherNameForClick ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoucherClick(voucherNameForClick);
                  }}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  {label}
                </Button>
              ) : (
                label
              )}
            </Box>
            <Tooltip title="Open voucher">
              <IconButton
                component={Link}
                to={`/marketing/vouchers/${voucherId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'userId',
      headerName: 'User',
      minWidth: 150,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const uid = params.row.userId;
        if (!uid) {
          return '-';
        }
        const userName = usersSummaryMap.get(uid)?.full_name;
        if (onUserIdClick) {
          return (
            <Button variant="text" size="small" onClick={() => onUserIdClick(uid)} sx={{ textTransform: 'none' }}>
              {userName ?? uid}
            </Button>
          );
        }
        return <CopyableUserLink label={userName ?? uid} userId={uid} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      minWidth: 180,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => <DateFormatterDetailed date={params.value} />,
    },
    {
      field: 'deletedAt',
      headerName: 'Deleted At',
      minWidth: 180,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => (params.value ? <DateFormatterDetailed date={params.value} /> : '-'),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      minWidth: 200,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => {
        const userName = usersSummaryMap.get(params.row.createdBy)?.full_name;
        return <CopyableUserLink label={userName ?? params.row.createdBy} userId={params.row.createdBy} />;
      },
    },
    {
      field: 'debug',
      headerName: 'Debug',
      minWidth: 120,
      ...voucherLogColumnSharedAttrs,
      renderCell: (params) => <DebugModal type="generic" title="Log Row" data={params.row} />,
    },
    { field: 'voucherLogId', headerName: 'Log ID', flex: 1, ...voucherLogColumnSharedAttrs },
    { field: 'voucherUserId', headerName: 'Voucher User ID', flex: 1, ...voucherLogColumnSharedAttrs },
  ];

  if (includeUnlogColumn && onUnlog) {
    cols.push({
      field: 'actions',
      headerName: '',
      width: 90,
      ...voucherLogColumnSharedAttrs,
      flex: undefined,
      renderCell: (params: GridRenderCellParams<VoucherLog>) => {
        if (params.row.deletedAt) {
          return null;
        }
        return (
          <Tooltip title="Unlog voucher">
            <IconButton
              aria-label="Unlog voucher"
              size="small"
              onClick={() => onUnlog(params.row)}
              disabled={unlogPending}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    });
  }

  return cols;
}
